<script lang="ts">
  import SelectInput from "@kontextwerk/web-sdk/ui/forms/SelectInput.svelte"
  import type {
    ContactOrderAppointmentRow,
    ContactOrderPostalToggleOption,
    RowCommunicationSelection,
  } from "../../../../../../_logic/planning-selection-contract"
  import { postalSelectionValue } from "../../../../../../_logic/channels"

  let {
    options,
    selection,
    preview,
    hasContactOrder = false,
    disabled = false,
    pending = false,
    onChange,
  }: {
    options: ContactOrderPostalToggleOption[]
    selection: RowCommunicationSelection["postal"]
    preview?: ContactOrderAppointmentRow["planningPreview"]
    hasContactOrder?: boolean
    disabled?: boolean
    pending?: boolean
    onChange: (value: string, dispatch: "MANUAL" | "AUTOMATIC") => void
  } = $props()

  type PostalOption = {
    value: string
    label: string
    subline: string
    recipientValue: string
    dispatch: "MANUAL" | "AUTOMATIC"
    selectable: boolean
    reason?: string | null
  }
  const automaticReason = $derived(
    preview?.automatic_postal_available
      ? null
      : (preview?.automatic_postal_disabled_reason ??
          "Automatische Zustellung ist derzeit nicht verfügbar."),
  )
  const selectOptions = $derived.by(() => {
    const recipients = [
      ...options.filter((option) => option.mode === "PERSONAL"),
      {
        value: "INDIVIDUAL",
        label: selection.manual_recipient
          ? `Individuell: ${selection.manual_recipient.name}`
          : "Individuell erfassen …",
        mode: "PERSONAL" as const,
        disabled: !options.some((option) => option.mode === "IMPERSONAL" && !option.disabled),
      },
      ...options.filter((option) => option.mode === "IMPERSONAL" || option.mode === "NOTICE"),
    ]

    return recipients.flatMap((option): PostalOption[] =>
      (option.mode === "PERSONAL" ? (["MANUAL", "AUTOMATIC"] as const) : (["MANUAL"] as const)).map(
        (dispatch) => ({
          value: JSON.stringify([option.value, dispatch]),
          label: "selectedLabel" in option ? (option.selectedLabel ?? option.label) : option.label,
          subline: dispatch === "AUTOMATIC" ? "Automatisch wenn Fällig" : "Manuell durch ToDO",
          recipientValue: option.value,
          dispatch,
          selectable: !option.disabled && !(dispatch === "AUTOMATIC" && automaticReason),
          reason: option.disabled
            ? "tooltip" in option
              ? option.tooltip
              : "Terminbriefe sind nicht verfügbar."
            : dispatch === "AUTOMATIC"
              ? automaticReason
              : null,
        }),
      ),
    )
  })
  const selectedValue = $derived(
    selection.mode === "DISABLED"
      ? ""
      : JSON.stringify([postalSelectionValue(selection), selection.dispatch_mode ?? "MANUAL"]),
  )
  const optionByValue = $derived(new Map(selectOptions.map((option) => [option.value, option])))
  const selectedOption = $derived(optionByValue.get(selectedValue))
</script>

<div class="postage-cell" aria-label="Briefzustellung" aria-busy={pending}>
  {#if hasContactOrder}
    <span>Bereits angelegt</span>
  {:else}
    <SelectInput
      ariaLabel="Brief an"
      placeholder="Briefempfänger wählen"
      value={selectedValue}
      options={selectOptions}
      clearable={false}
      surfaceTone="light"
      disabled={disabled || pending}
      classList="postage-select"
      selectionRenderer={selectedRecipient}
      itemRenderer={recipientOption}
      onValueChange={(value) => {
        const option = optionByValue.get(String(value))

        if (option?.selectable) onChange(option.recipientValue, option.dispatch)
      }}
    />
    {#if selectedOption?.reason}<small role="status">{selectedOption.reason}</small>{/if}
  {/if}
</div>

{#snippet optionLabel(option: PostalOption)}
  <span class="postal-option">
    <strong>{option.label}</strong>
    <span>{option.subline}</span>
    {#if option.reason}<small>{option.reason}</small>{/if}
  </span>
{/snippet}
{#snippet selectedRecipient({ selection })}
  {@const option = optionByValue.get(String(selection?.value ?? ""))}
  {#if option}
    <span class="selected-recipient">
      {@render optionLabel({ ...option, reason: null })}
    </span>
  {/if}
{/snippet}
{#snippet recipientOption({ item })}
  {@const option = optionByValue.get(String(item.value))}
  {#if option}
    <span
      role="group"
      aria-label={`${option.label}. ${option.subline}`}
      aria-disabled={!option.selectable}
    >
      {@render optionLabel(option)}
    </span>
  {/if}
{/snippet}

<style>
  .postage-cell {
    width: 100%;
    min-width: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.25rem;
  }
  .postage-cell :global(.postage-select) {
    --height: 52px;
    --selected-item-padding: 0;
    --item-height: auto;
  }
  .postal-option {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
    padding-block: 0.2rem;
  }
  .postal-option strong {
    font-size: 0.85rem;
    overflow-wrap: anywhere;
  }
  .selected-recipient {
    display: block;
    min-width: 0;
    width: 100%;
  }
  .selected-recipient .postal-option strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .postal-option > span,
  small {
    font-size: 0.75rem;
    overflow-wrap: anywhere;
  }
  small {
    color: var(--text-100);
  }
</style>
